import {
    Flex,
    useStyles,
    Pagination,
    PaginationItem,
} from '@archipro-design/aria';
import type {
    DropdownProps,
    PaginationItemCounterProps,
    AppDisplayModeType,
} from '@archipro-design/aria';

import { Link, useLocation, useSearchParams } from '@remix-run/react';
import * as S from './BottomPagination.style';
import { trimPaginationURL } from '~/modules/pagination/util/trim-pagination-url';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '../../util/constants';

export interface BasePaginatedProps {
    totalCount: number;
    totalPage: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}
interface PaginatedItemProps extends BasePaginatedProps {
    itemName: string;
    appDisplayMode?: AppDisplayModeType;
    wrapperClassName?: string;
    onPageChange?: (page: number) => void;
}

const BottomPagination: React.FC<PaginatedItemProps> = ({
    itemName,
    totalCount,
    totalPage,
    currentPage,
    pageSize,
    hasPreviousPage,
    hasNextPage,
    appDisplayMode = { mobile: true, desktop: false },
    wrapperClassName,
    onPageChange,
}) => {
    const { css } = useStyles();

    const location = useLocation();

    const [searchParams, setSearchParams] = useSearchParams();

    const renderPaginationLink = (page: number | null) => {
        return trimPaginationURL(page, searchParams, location);
    };

    const handlePageSizeChange = (e: unknown, data: DropdownProps) => {
        if (!data.value) return;

        const pageSize = data.value;

        searchParams.set('pageSize', pageSize.toString());
        searchParams.set('page', '1');
        setSearchParams(searchParams);
    };

    return (
        <Flex
            className={wrapperClassName ?? css(S.PaginationWrapper)}
            hAlign="center"
            fill={true}
        >
            {totalPage > 1 && (
                <Pagination
                    appDisplayMode={appDisplayMode}
                    prevButton={{}}
                    nextButton={{}}
                    renderItem={(item) => {
                        const disablePrev =
                            item.type === 'previous' && !hasPreviousPage;
                        const disableNext =
                            item.type === 'next' && !hasNextPage;

                        if (disablePrev || disableNext) {
                            return PaginationItem.create(
                                {
                                    content: item,
                                },
                                { generateKey: true },
                            );
                        }

                        return PaginationItem.create({
                            as: Link,
                            key: `paginationItem-${item.type}-${item.page}`,
                            prefetch: 'intent',
                            to: `${renderPaginationLink(item.page)}#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                            content: item,
                        });
                    }}
                    defaultPage={currentPage}
                    count={totalPage}
                    counter={{
                        range: {
                            from: (currentPage - 1) * pageSize + 1,

                            to:
                                currentPage * pageSize > totalCount
                                    ? totalCount
                                    : currentPage * pageSize,
                        },
                        total: totalCount,
                        textRenderer: (
                            range: PaginationItemCounterProps['range'],
                            total: PaginationItemCounterProps['total'],
                        ) =>
                            `${range.from} - ${range.to} of ${total} ${itemName}`,
                    }}
                    totalCount={totalCount}
                    pageSize={pageSize}
                    itemsPerPage={{
                        label: `${itemName} per page:`,
                        dropdown: {
                            checkable: false,
                            defaultValue: pageSize,
                            onChange: handlePageSizeChange,
                        },
                    }}
                    onChange={(_, page) => {
                        if (page !== null) {
                            onPageChange?.(page);
                        }
                    }}
                />
            )}
        </Flex>
    );
};

export default BottomPagination;
